import React, { useEffect } from 'react';
import { AuthProvider, useAuth } from './components/context/AuthContext';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { NotificationProvider } from './components/ui/Notification';
import Router from './components/core/Router';
import { fcmService } from './services/fcmService';
import { createLogger } from './utils/logger';
import { isIOSOrMobileSafari } from './utils/browserUtils';

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim() || '';

const logger = createLogger('App');

// FCM Initialization Component - skip on iOS Safari (limited support, can cause blank screen when logged in)
function FCMInitializer() {
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      fcmService.clearToken();
      return;
    }
    if (isIOSOrMobileSafari()) return;
    // Force service worker update to clear any cached versions
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          if (registration.active?.scriptURL?.includes('firebase-messaging-sw.js')) {
            logger.debug('Updating FCM service worker');
            registration.update();
          }
        });
      });
    }

    if (currentUser) {
      // Request FCM permission and get token
      fcmService
        .requestPermissionAndGetToken()
        .then((token) => {
          if (token) {
            logger.debug('FCM token obtained for user', currentUser.uid);
          }
        })
        .catch((error) => {
          logger.error('FCM initialization error', error);
        });

      // Check and refresh token periodically (every 24 hours)
      const tokenCheckInterval = setInterval(() => {
        fcmService.checkAndRefreshToken().catch((error) => {
          logger.error('Token refresh check error', error);
        });
      }, 24 * 60 * 60 * 1000); // 24 hours

      // Also check once on mount after a short delay
      const initialCheckTimeout = setTimeout(() => {
        fcmService.checkAndRefreshToken().catch((error) => {
          logger.error('Initial token refresh check error', error);
        });
      }, 5000); // 5 seconds

      return () => {
        clearInterval(tokenCheckInterval);
        clearTimeout(initialCheckTimeout);
      };
    }
  }, [currentUser]);

  return null;
}

function App() {
  const app = (
    <HelmetProvider>
      <NotificationProvider>
        <AuthProvider>
          <FCMInitializer />
          <BrowserRouter>
            <Router />
          </BrowserRouter>
        </AuthProvider>
      </NotificationProvider>
    </HelmetProvider>
  );

  return googleClientId ? (
    <GoogleOAuthProvider clientId={googleClientId}>{app}</GoogleOAuthProvider>
  ) : (
    app
  );
}
export default App;
